import React, { useState } from 'react';
import { Box, Button, FormHelperText, IconButton, Typography } from '@mui/material';
import { RiEyeFill, RiEyeOffFill } from 'react-icons/ri';
import HighlightedText from 'components/HighlightedText';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { LoadingData } from 'common/types';
import { useDispatch } from 'react-redux';
import * as NotificationActions from 'actions/NotificationActions';
import { useTranslation } from 'services/localization';
import { Callable } from 'common/frontend/callable';
import Spinner from 'common/components/Spinner';
import useLocalFormat from 'hooks/useShopFormat';
import BackdropDialog from 'common/components/BackdropDialog';

export interface Props {
	merchantId: string;
	secret: string | undefined;
	secretCreated: string | undefined;
}

const MerchantSecret = (props: Props) => {
	const dispatch = useDispatch();
	const { localFormat } = useLocalFormat();
	const { t } = useTranslation();
	const [secret, setSecret] = useState<
		LoadingData<{ secret: string | undefined; created: string | undefined }>
	>({
		loading: false,
		error: null,
		data: { secret: props.secret, created: props.secretCreated },
	});
	const [showField, setShowField] = useState<boolean>(false);
	const [showSecretDeleteConfirm, setShowSecretDeleteConfirm] = useState<boolean>(false);

	const createSecret = async () => {
		try {
			setSecret({ loading: true, data: null, error: null });
			const result = await Callable.integrations.solteq.createMerchantSecret({
				merchantId: props.merchantId,
			});
			setSecret({ loading: false, data: result, error: null });
			setShowField(true);
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.error'),
					variant: 'error',
				}),
			);
			setSecret({ loading: false, data: null, error: t('common:errors.error') });
		}
	};

	const deleteSecret = async () => {
		try {
			setSecret((secret) => ({ error: null, data: secret.data, loading: true }));
			await Callable.integrations.solteq.deleteMerchantSecret({
				merchantId: props.merchantId,
			});
			setSecret({ loading: false, data: { secret: undefined, created: undefined }, error: null });
			setShowField(true);
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.error'),
					variant: 'error',
				}),
			);
			setSecret((secret) => ({
				error: null,
				data: secret.data ?? { secret: undefined, created: undefined },
				loading: false,
			}));
		}
	};

	return (
		<>
			{!secret.data?.secret ? (
				<Button variant="outlined" onClick={createSecret} disabled={secret.loading} sx={{ mt: 1 }}>
					{secret.loading ? (
						<Spinner padding={8} />
					) : (
						t('integrations.cloudPos.createSecret', 'Create secret')
					)}
				</Button>
			) : (
				<>
					<HighlightedText
						text={showField ? secret.data.secret : '•••••••••••'}
						endAdornment={
							<>
								{!!showField && (
									<CopyToClipboardIcon text={secret.data.secret} showTooltip showCopyNotification />
								)}
								<IconButton
									aria-label="toggle visibility"
									onClick={() => setShowField(!showField)}
									edge="end"
									size="small"
								>
									{showField ? <RiEyeOffFill color="#848484" /> : <RiEyeFill color="#848484" />}
								</IconButton>
							</>
						}
					/>
					{secret.data.created && (
						<FormHelperText>
							{t('created', 'Created')}: {localFormat(secret.data.created, 'DD.MM.YYYY, HH:mm')}
						</FormHelperText>
					)}
					<Button
						variant="text"
						onClick={() => setShowSecretDeleteConfirm(true)}
						color="error"
						sx={{ mt: 1 }}
						disabled={secret.loading}
					>
						{secret.loading ? (
							<Spinner padding={8} />
						) : (
							t('integrations.cloudPos.deleteSecret', 'Delete secret')
						)}
					</Button>
				</>
			)}
			{!!showSecretDeleteConfirm && (
				<BackdropDialog
					isOpen={!!showSecretDeleteConfirm}
					onClose={() => setShowSecretDeleteConfirm(false)}
					label={`${t('integrations.cloudPos.deleteSecret', 'Delete secret')}?`}
					body={
						<Box>
							<Typography variant="body2">
								{t(
									'integrations.cloudPos.deleteSecretBody',
									'Are you sure you want to delete the secret',
								)}
								?
							</Typography>
						</Box>
					}
					variant="danger"
					width="xs"
					confirm={{
						label: t('common:actions.delete'),
						callback: () => deleteSecret(),
					}}
					cancel={{
						label: t('common:actions.cancel'),
					}}
				/>
			)}
		</>
	);
};

export default MerchantSecret;
